import { z } from 'zod';
import { ROOM_CODE_RE } from './config';
import { textSchema as text } from './schema-primitives';

/**
 * Competitive queue WebSocket messages between the browser and the Matchmaker
 * (docs/decisions/COMPETITIVE-RANKED.md). Hidden MMR never crosses this boundary.
 */

const proposalId = z.string().min(1).max(64);

export const queueClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('enqueue'), profileCredential: z.string().min(1).max(256) }),
  z.object({ t: z.literal('cancel') }),
  z.object({ t: z.literal('accept'), proposalId }),
  z.object({ t: z.literal('decline'), proposalId }),
]);
export type QueueClientMessage = z.infer<typeof queueClientMessageSchema>;

const QUEUE_REFUSALS = ['auth', 'blocked', 'active', 'duplicate', 'unavailable'] as const;
export type QueueRefusal = (typeof QUEUE_REFUSALS)[number];

export const queueServerMessageSchema = z.discriminatedUnion('t', [
  /** Searching since `since` (server milliseconds); also sent after a failed proposal returns you to the queue. */
  z.object({ t: z.literal('queued'), since: z.number() }),
  z.object({
    t: z.literal('proposal'),
    id: proposalId,
    endAt: z.number(),
    opponent: z.object({ name: text, avatar: text, bot: z.boolean() }),
  }),
  z.object({ t: z.literal('matched'), roomCode: z.string().regex(ROOM_CODE_RE) }),
  /** You declined or let a proposal expire; the queue socket closes after this. */
  z.object({ t: z.literal('left') }),
  z.object({
    t: z.literal('refused'),
    reason: z.enum(QUEUE_REFUSALS),
    msg: text,
    /** `active`: the unfinished ranked match to return to. */
    roomCode: z.string().regex(ROOM_CODE_RE).optional(),
  }),
]);
export type QueueServerMessage = z.infer<typeof queueServerMessageSchema>;
